import { useTranslation } from '@hooks/useTranslation';

/** Maps the backend's `extensions.code` (see `docs/architecture.md`'s Sprint 32 section) to a
 * key on the `assistant` locale section — used by every mutation hook below so a synchronous
 * rejection (busy/cap/stale/etc.) shows a localized message instead of the raw English backend
 * string. */
const ERROR_CODE_TO_KEY: Record<string, keyof ReturnType<typeof useTranslation<'assistant'>>> = {
  ASSISTANT_NOT_CONFIGURED: 'errorNotConfigured',
  ASSISTANT_BUSY: 'errorBusy',
  ASSISTANT_QUOTA_EXCEEDED: 'errorDailyMessageCap',
  ASSISTANT_NOT_FOUND: 'errorNotFound',
  ASSISTANT_NO_PENDING_ACTION: 'errorNoPendingAction',
  ASSISTANT_ACTION_STALE: 'errorActionStale',
  ASSISTANT_INVALID_TIMEZONE: 'errorInvalidTimezone',
  ASSISTANT_MESSAGE_TOO_LONG: 'errorMessageTooLong',
  ASSISTANT_TIMEOUT: 'errorTimeout',
  ASSISTANT_REFUSED: 'errorRefused',
  ASSISTANT_TRUNCATED: 'errorTruncated',
  ASSISTANT_RATE_LIMITED: 'errorRateLimited',
  // Sprint 37, story-202. Billing and auth failures share ONE generic copy: never vendor, key or
  // billing wording, and never the server's `errorMessage`.
  ASSISTANT_BILLING: 'errorUnavailable',
  ASSISTANT_AUTH: 'errorUnavailable',
  ASSISTANT_UPSTREAM_ERROR: 'errorUpstream',
};

const MAX_TURNS_KEY = 'errorMaxTurns' as const;

export function getAssistantErrorCode(error: any): string | undefined {
  return error?.response?.errors?.[0]?.extensions?.code;
}

export function getAssistantErrorMessage(
  error: any,
  tAssistant: ReturnType<typeof useTranslation<'assistant'>>,
): string {
  const code = getAssistantErrorCode(error);
  const reason = error?.response?.errors?.[0]?.extensions?.reason;
  if (code === 'ASSISTANT_QUOTA_EXCEEDED' && reason === 'MAX_TURNS_PER_CONVERSATION') {
    return tAssistant[MAX_TURNS_KEY];
  }
  const key = code ? ERROR_CODE_TO_KEY[code] : undefined;
  return key ? (tAssistant[key] as string) : tAssistant.errorUpstream;
}

/**
 * Sprint 37, story-202. Copy for an in-turn failure, i.e. `conversation.errorCode` (a stable
 * `AssistantTurnErrorCode`), which travels on the conversation row and NOT on a GraphQL
 * `extensions.code`. Anything unmapped or absent falls back to the generic upstream copy; the
 * server's `errorMessage` is never displayed.
 */
export function getAssistantTurnErrorMessage(
  errorCode: string | null | undefined,
  tAssistant: ReturnType<typeof useTranslation<'assistant'>>,
): string {
  const key = errorCode ? ERROR_CODE_TO_KEY[errorCode] : undefined;
  return key ? (tAssistant[key] as string) : tAssistant.errorUpstream;
}
